import {
  createBattle,
  isCooldownReady,
  pickStubNpcMove,
  tick,
  tryCastMove,
  type BattleDataContext,
  type BattleState,
} from '@pokebot/shared/battle';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createClientBattleDataContext } from './client-data-context';
import { DEV_BATTLE_INITIAL } from './dev-fixture';
import './BattleScreen.css';

function formatCdSeconds(seconds: number): string {
  return seconds.toFixed(1);
}

function hpFillClass(ratio: number): string {
  if (ratio <= 0.25) return 'battle-screen__hp-fill battle-screen__hp-fill--critical';
  if (ratio <= 0.5) return 'battle-screen__hp-fill battle-screen__hp-fill--low';
  return 'battle-screen__hp-fill';
}

interface BattleScreenProps {
  onBack: () => void;
}

function CombatantPanel({
  mon,
  ctx,
  variant,
}: {
  mon: BattleState['player'];
  ctx: BattleDataContext;
  variant: 'player' | 'opponent';
}) {
  const species = ctx.getSpecies(mon.speciesId);
  const ratio = mon.stats.maxHp > 0 ? mon.currentHp / mon.stats.maxHp : 0;
  const pct = Math.max(0, Math.min(100, ratio * 100));

  return (
    <section className={`battle-screen__panel battle-screen__panel--${variant}`}>
      <h2 className="battle-screen__name">
        {species.name} Lv.{mon.level}
      </h2>
      <div className="battle-screen__hp-bar" aria-hidden>
        <div className={hpFillClass(ratio)} style={{ width: `${pct}%` }} />
      </div>
      <p className="battle-screen__hp-text">
        {mon.currentHp} / {mon.stats.maxHp}
      </p>
    </section>
  );
}

export function BattleScreen({ onBack }: BattleScreenProps) {
  const { t } = useTranslation();
  const ctx = useMemo(() => createClientBattleDataContext(), []);
  const battleRef = useRef<BattleState>(createBattle(DEV_BATTLE_INITIAL, ctx));
  const [, bump] = useState(0);
  const forceUpdate = useCallback(() => bump((n) => n + 1), []);

  const battle = battleRef.current;
  const battleEnded = battle.status !== 'active';
  const globalCd = battle.player.globalRemainingCd;
  const globalGate = !isCooldownReady(globalCd);

  useEffect(() => {
    if (battleEnded) return undefined;

    const intervalMs = ctx.balance.battleTickSeconds * 1000;
    const id = window.setInterval(() => {
      const state = battleRef.current;
      if (state.status !== 'active') {
        window.clearInterval(id);
        forceUpdate();
        return;
      }

      tick(state, undefined, ctx);

      const opponentMoveId = pickStubNpcMove(state, 'opponent');
      if (opponentMoveId) {
        tryCastMove(state, 'opponent', { kind: 'move', moveId: opponentMoveId }, ctx);
      }

      forceUpdate();
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [battleEnded, forceUpdate, ctx]);

  const castPlayerMove = (moveId: string) => {
    if (battleRef.current.status !== 'active') return;

    const result = tryCastMove(
      battleRef.current,
      'player',
      { kind: 'move', moveId },
      ctx,
    );
    if (result.ok) forceUpdate();
  };

  return (
    <div className="battle-screen">
      <header className="battle-screen__header">
        <h1>{t('battle.title')}</h1>
        <button type="button" className="battle-screen__back" onClick={onBack}>
          {t('battle.back')}
        </button>
      </header>

      <CombatantPanel mon={battle.opponent} ctx={ctx} variant="opponent" />

      {battleEnded ? (
        <p className="battle-screen__result" role="status">
          {battle.status === 'player-won' ? t('battle.win') : t('battle.lose')}
        </p>
      ) : globalGate ? (
        <p className="battle-screen__hint">{t('battle.globalCd', { seconds: formatCdSeconds(globalCd) })}</p>
      ) : null}

      <CombatantPanel mon={battle.player} ctx={ctx} variant="player" />

      <div className="battle-screen__moves">
        {battle.player.moveIds.map((moveId) => {
          const remaining = battle.player.cooldowns.remainingCd[moveId] ?? 0;
          const onCd = !isCooldownReady(remaining);
          const disabled = battleEnded || globalGate || onCd;
          const label = t('battle.moveFallback', { id: moveId.replace(/_/g, ' ') });

          return (
            <button
              key={moveId}
              type="button"
              className="battle-screen__move"
              disabled={disabled}
              onClick={() => castPlayerMove(moveId)}
            >
              {label}
              {onCd && !battleEnded ? (
                <span className="battle-screen__move-cd">
                  {t('battle.moveCd', { seconds: formatCdSeconds(remaining) })}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
